import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ScopedLibrary } from "@/components/ScopedLibrary";
import { StatusBadge, ProgressBar, EmptyState, fmtDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  GraduationCap, BookOpen, Award, Search, FileText, Download, Eye,
  ExternalLink, Link2, ClipboardList, Loader2, Calendar, UserCheck, Eye as EyeIcon, Sparkles,
} from "lucide-react";

const isGraduate = (o) =>
  !!o && /graduate|certified|complete|eligible/i.test(o) && !/additional|needs|review/i.test(o);

export function EmployeePortal({ preview = false, onExit }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("training");

  const { data: sessions = [], isLoading: loadingSessions } = useQuery({
    queryKey: ["my-sessions"],
    queryFn: async () => (await api.get("/training-sessions")).data,
  });

  const { data: certificates = [], isLoading: loadingCerts } = useQuery({
    queryKey: ["my-certificates"],
    queryFn: async () => (await api.get("/me/certificates")).data,
    enabled: !preview,
  });

  return (
    <div className="space-y-6" data-testid="employee-portal">
      {preview && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-accent/40 bg-accent/10 px-4 py-3" data-testid="employee-preview-banner">
          <div className="flex items-center gap-2 text-sm">
            <EyeIcon size={16} className="text-accent" />
            <span className="font-medium">Previewing the Employee Portal</span>
            <span className="text-muted-foreground hidden sm:inline">— this is how staff experience their portal.</span>
          </div>
          <Button size="sm" variant="secondary" onClick={onExit} data-testid="exit-employee-preview">Exit preview</Button>
        </div>
      )}

      <div className="flex items-center gap-4">
        <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-primary/10 text-primary shrink-0">
          <GraduationCap size={26} />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">My Portal</h1>
          <p className="text-muted-foreground mt-0.5">
            Welcome, {user?.name?.split(" ")[0]}. Track your training, browse SOPs, and view your certificates.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="employee-portal-tabs">
          <TabsTrigger value="training" data-testid="tab-my-training" className="gap-1.5"><ClipboardList size={15} /> My Training</TabsTrigger>
          <TabsTrigger value="library" data-testid="tab-my-library" className="gap-1.5"><BookOpen size={15} /> SOP Library</TabsTrigger>
          <TabsTrigger value="certificates" data-testid="tab-my-certificates" className="gap-1.5"><Award size={15} /> My Certificates</TabsTrigger>
        </TabsList>

        {/* My Training */}
        <TabsContent value="training" className="mt-4">
          {loadingSessions ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin text-muted-foreground" /></div>
          ) : sessions.length === 0 ? (
            <Card><EmptyState icon={ClipboardList} title="No training assigned yet" desc="When a trainer starts your onboarding, it will appear here so you can follow your progress." /></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="my-training-list">
              {sessions.map((s) => (
                <Card key={s.id} className="p-5 space-y-4" data-testid={`my-training-card-${s.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-heading font-semibold text-lg leading-tight">{s.training_type}</h3>
                      <p className="text-sm text-muted-foreground">{s.trainee_position}</p>
                    </div>
                    <StatusBadge status={s.status} />
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                      <span>Progress</span>
                      <span className="font-mono">{s.percent_complete}%</span>
                    </div>
                    <ProgressBar value={s.percent_complete} />
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <UserCheck size={14} /> <span className="truncate">{s.trainer_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar size={14} /> Started {fmtDate(s.training_start_date)}
                    </div>
                  </div>
                  {s.status === "completed" && (
                    <div className="flex flex-wrap items-center gap-2 pt-1 border-t">
                      <span className="text-xs text-muted-foreground">Completed {fmtDate(s.completed_at)}</span>
                      {s.outcome && (
                        <Badge variant="outline" className={isGraduate(s.outcome)
                          ? "bg-success/15 text-success border-success/30"
                          : "bg-warning/15 text-warning border-warning/30"} data-testid={`outcome-badge-${s.id}`}>
                          {s.outcome}
                        </Badge>
                      )}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* SOP Library */}
        <TabsContent value="library" className="mt-4">
          <ScopedLibrary asEmployee={preview} />
        </TabsContent>

        {/* My Certificates */}
        <TabsContent value="certificates" className="mt-4">
          {loadingCerts ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin text-muted-foreground" /></div>
          ) : certificates.length === 0 ? (
            <Card data-testid="my-certificates-placeholder">
              <EmptyState icon={Award} title="Your certificates will appear here" desc="When you pass a training program, your certificate is issued here for you to view and download." />
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="my-certificates-list">
              {certificates.map((c) => (
                <Card key={c.id} className={`relative overflow-hidden p-5 border-accent/30 ${c.revoked ? "opacity-60" : ""}`} data-testid={`certificate-card-${c.id}`}>
                  <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-accent/10" />
                  <div className="relative flex items-start gap-4">
                    <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-success/15 text-success shrink-0">
                      <Sparkles size={22} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-heading font-bold leading-tight">{c.certificate_type_name}</h3>
                      <p className="text-xs text-muted-foreground font-mono mt-1">{c.certificate_number} · Issued {fmtDate(c.issued_at)}</p>
                      {c.revoked
                        ? <Badge variant="outline" className="mt-2 bg-destructive/10 text-destructive border-destructive/30">Revoked</Badge>
                        : (
                          <div className="mt-4">
                            <Button size="sm" className="gap-1.5" onClick={() => window.open(`${API}/certificates/${c.id}/pdf`, "_blank")} data-testid={`download-certificate-${c.id}`}>
                              <Download size={14} /> Download certificate (PDF)
                            </Button>
                          </div>
                        )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
